/**
 * The timeline beside a pulled-out mannequin: a swimmer plot of the subject's whole course on
 * one canvas, study days across (day 0 = first dose), one lane per kind of record. Every bar
 * and mark is pointable for its details and selectable to open the record in the profile.
 */
import type * as THREE from 'three';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  fillRoundRect,
  fitSize,
  font,
  hexToRgba,
  text,
  wrapText,
} from '../dashboards/charts.ts';
import type { StudyView } from '../dashboards/StudyView.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { REVIEW, type PanelPose } from './layout.ts';
import { swimmerModel, tickStep, type SwimItem, type SwimLane, type SwimModel } from './swimmer.ts';

const PX_W = 1120;
const PX_H = 736;
const GUTTER = 232;
const RIGHT = 26;
const TOP = 104;
const BOTTOM = 78;
/** Bar labels are drawn at this size whether inside or beside the bar. */
const LABEL_SIZE = 14;

/** A bar or mark placed in a row, with where its label goes (if it has one that shows). */
interface Placed {
  item: SwimItem;
  inside: boolean;
  labelX?: number;
}

export class SwimmerPlot {
  readonly panel: CanvasPanel;
  private subjectId: string | undefined;
  private model: SwimModel | undefined;

  constructor(
    private readonly view: StudyView,
    private readonly interactive: InteractiveSystem,
    private readonly handlers: {
      open(subjectId: string, path: string[]): void;
      tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
    },
  ) {
    this.panel = new CanvasPanel({
      width: REVIEW.swimmer.width,
      height: REVIEW.swimmer.height,
      pixelWidth: PX_W,
      pixelHeight: PX_H,
      name: 'ward-swimmer',
      resolution: 1.5,
    });
    (this.panel.mesh.material as THREE.MeshBasicMaterial).transparent = true;
    this.panel.mesh.visible = false;
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.select(region);
    this.panel.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.panel.attach(interactive);
  }

  get mesh(): THREE.Mesh {
    return this.panel.mesh;
  }

  get subject(): string | undefined {
    return this.subjectId;
  }

  show(subjectId: string, pose: PanelPose): void {
    this.subjectId = subjectId;
    this.model = swimmerModel(this.view, subjectId);
    this.panel.mesh.position.set(pose.x, pose.y, pose.z);
    this.panel.mesh.rotation.set(0, pose.yaw, 0);
    this.panel.mesh.scale.setScalar(pose.scale);
    this.panel.mesh.visible = true;
    this.panel.invalidate();
  }

  /** Moves the plot (a dragged cockpit) without redrawing it. */
  move(pose: PanelPose): void {
    this.panel.mesh.position.set(pose.x, pose.y, pose.z);
    this.panel.mesh.rotation.set(0, pose.yaw, 0);
  }

  hide(): void {
    this.subjectId = undefined;
    this.model = undefined;
    this.panel.mesh.visible = false;
  }

  /** Data changed (a query raised or answered): rebuild the lanes. */
  refresh(): void {
    if (!this.subjectId) return;
    this.model = swimmerModel(this.view, this.subjectId);
    this.panel.invalidate();
  }

  update(): void {
    if (this.panel.mesh.visible) this.panel.update();
  }

  allRegions(): { id: string; data: unknown }[] {
    return this.panel.mesh.visible
      ? this.panel.allRegions().map((r) => ({ id: r.id, data: r.data }))
      : [];
  }

  selectRegion(id: string): boolean {
    const region = this.panel.allRegions().find((r) => r.id === id);
    if (!region) return false;
    this.select(region);
    return true;
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const m = this.model;
    const s = this.subjectId ? this.view.subject(this.subjectId) : undefined;
    if (!m || !s) return;
    fillRoundRect(ctx, { x: 0, y: 0, w: PX_W, h: PX_H }, 18, PALETTE.surface);
    text(ctx, `${s.id} · timeline`, 22, 38, { size: 26, weight: 800 });
    const sub = `Day 0 = ${m.day0Label} (${m.day0Date}) · data cut ${m.cutDate} (day ${m.cutDay})`;
    text(ctx, sub, 22, 66, {
      size: fitSize(ctx, sub, PX_W - 44, 14, 10),
      colour: PALETTE.muted,
    });
    const x0 = GUTTER;
    const x1 = PX_W - RIGHT;
    const span = Math.max(1, m.endDay - m.startDay);
    const xOf = (day: number) => x0 + ((day - m.startDay) / span) * (x1 - x0);
    const top = TOP;
    const bottom = PX_H - BOTTOM;
    // Row heights: every lane gets at least one row; shrink rows to fit the plot.
    const laneGap = 6;
    const laneRows = m.lanes.map((lane) => this.layoutRows(ctx, lane, xOf));
    const rowsTotal = laneRows.reduce((n, rows) => n + Math.max(1, rows.length), 0);
    const rh = Math.max(
      16,
      Math.min(52, (bottom - top - laneGap * m.lanes.length) / Math.max(1, rowsTotal)),
    );
    // Grid: ticks in study days.
    const step = tickStep(span);
    ctx.strokeStyle = PALETTE.grid;
    ctx.lineWidth = 1;
    for (let day = Math.ceil(m.startDay / step) * step; day <= m.endDay; day += step) {
      const x = xOf(day);
      ctx.beginPath();
      ctx.moveTo(x, top - 4);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      text(ctx, `${day}`, x, bottom + 20, { size: 13, colour: PALETTE.muted, align: 'center' });
    }
    text(ctx, 'Study day', 22, bottom + 20, { size: 13, colour: PALETTE.muted });
    // Day 0 and the data cut.
    const marker = (day: number, colour: string, label: string) => {
      if (day < m.startDay || day > m.endDay) return;
      const x = xOf(day);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(x, top - 10);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      // Right of the line, or left of it when that would run off the panel.
      ctx.font = font(13, 700);
      const tw = ctx.measureText(label).width;
      if (x + 4 + tw <= PX_W - 12)
        text(ctx, label, x + 4, top - 14, { size: 13, weight: 700, colour });
      else text(ctx, label, x - 4, top - 14, { size: 13, weight: 700, colour, align: 'right' });
    };
    marker(0, PALETTE.teal, 'Day 0');
    marker(m.cutDay, PALETTE.amber, 'Data cut');
    // Lanes.
    let y = top;
    m.lanes.forEach((lane, li) => {
      const placedRows = laneRows[li]!;
      const rows = Math.max(1, placedRows.length);
      const h = rows * rh;
      if (li % 2 === 0) {
        ctx.fillStyle = PALETTE.faint;
        ctx.fillRect(12, y - 2, PX_W - 24, h + 4);
      }
      text(ctx, lane.title, 22, y + Math.min(h, rh) / 2 + 5, {
        size: fitSize(ctx, lane.title, GUTTER - 82, 16, 12, 700),
        weight: 700,
      });
      text(ctx, lane.count ? `${lane.count}` : 'none', GUTTER - 14, y + Math.min(h, rh) / 2 + 5, {
        size: 13,
        colour: PALETTE.muted,
        align: 'right',
      });
      placedRows.forEach((row, ri) => {
        const cy = y + ri * rh + rh / 2;
        row.forEach((placed, ii) => {
          const next = row[ii + 1];
          this.drawItem(
            ctx,
            panel,
            placed,
            `item:${li}:${ri}:${ii}`,
            cy,
            rh,
            xOf,
            next && xOf(next.item.start),
            lane.title,
          );
        });
      });
      y += h + laneGap;
    });
    // Key, wrapped rather than cut.
    ctx.font = font(12, 500);
    const key = wrapText(
      ctx,
      'Point at anything for details, select it to open the record. Visits: filled done in window, amber out of window, red ring missed, grey ring scheduled. Bars: AEs coloured by grade, red outline serious; navy con meds. Arrows: ongoing.',
      PX_W - 44,
    );
    key.forEach((line, i) =>
      text(ctx, line, 22, PX_H - 12 - (key.length - 1 - i) * 16, {
        size: 12,
        colour: PALETTE.muted,
      }),
    );
  }

  /**
   * Rows for a lane, measured in pixels with each bar's whole label, so a label never has to
   * be cut: inside the bar when it fits, else beside it (right, or left near the edge), and
   * a bar whose label would collide moves to a new row. Study bars only label inside.
   */
  private layoutRows(
    ctx: CanvasRenderingContext2D,
    lane: SwimLane,
    xOf: (day: number) => number,
  ): Placed[][] {
    ctx.font = font(LABEL_SIZE, 600);
    const place = (item: SwimItem): { p: Placed; x0: number; x1: number } => {
      const x = xOf(item.start);
      const xe = Math.max(x + 5, xOf(item.end ?? item.start));
      const left = x - (item.arrowLeft ? 10 : 0) - (item.mark === 'bar' ? 0 : 8);
      const right = xe + (item.arrowRight ? 10 : 0) + (item.mark === 'bar' ? 0 : 8);
      if (item.mark !== 'bar' || !item.label)
        return { p: { item, inside: false }, x0: left, x1: right };
      const tw = ctx.measureText(item.label).width;
      if (tw + 10 <= xe - x) return { p: { item, inside: true }, x0: left, x1: right };
      if (lane.title === 'Study') return { p: { item, inside: false }, x0: left, x1: right };
      if (right + 6 + tw <= PX_W - 12)
        return { p: { item, inside: false, labelX: right + 4 }, x0: left, x1: right + 6 + tw };
      return { p: { item, inside: false, labelX: left - 6 - tw }, x0: left - 8 - tw, x1: right };
    };
    const repack = lane.title === 'Adverse events' || lane.title === 'Con meds';
    if (!repack) return lane.rows.map((row) => row.map((item) => place(item).p));
    const all = lane.rows
      .flat()
      .map(place)
      .sort((a, b) => a.x0 - b.x0);
    const rows: { end: number; items: Placed[] }[] = [];
    for (const e of all) {
      const row = rows.find((r) => r.end + 8 <= e.x0);
      if (row) {
        row.items.push(e.p);
        row.end = e.x1;
      } else rows.push({ end: e.x1, items: [e.p] });
    }
    return rows.map((r) => r.items);
  }

  private drawItem(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    placed: Placed,
    id: string,
    cy: number,
    rh: number,
    xOf: (day: number) => number,
    nextX: number | undefined,
    lane: string,
  ): void {
    const item = placed.item;
    const hovered = panel.isHovered(id);
    const x = xOf(item.start);
    let region: { x: number; y: number; w: number; h: number };
    if (item.mark === 'bar') {
      const xe = Math.max(x + 5, xOf(item.end ?? item.start));
      const bh = Math.max(8, rh * 0.62);
      const r = { x, y: cy - bh / 2, w: xe - x, h: bh };
      fillRoundRect(
        ctx,
        r,
        Math.min(5, bh / 2),
        hovered ? hexToRgba(item.colour, 1) : hexToRgba(item.colour, 0.85),
      );
      if (item.outline || hovered) {
        ctx.strokeStyle = hovered ? PALETTE.text : item.outline!;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.roundRect(r.x, r.y, r.w, r.h, Math.min(5, bh / 2));
        ctx.stroke();
      }
      ctx.fillStyle = item.colour;
      if (item.arrowRight) {
        ctx.beginPath();
        ctx.moveTo(xe + 1, cy - bh / 2);
        ctx.lineTo(xe + 9, cy);
        ctx.lineTo(xe + 1, cy + bh / 2);
        ctx.fill();
      }
      if (item.arrowLeft) {
        ctx.beginPath();
        ctx.moveTo(x - 1, cy - bh / 2);
        ctx.lineTo(x - 9, cy);
        ctx.lineTo(x - 1, cy + bh / 2);
        ctx.fill();
      }
      // Labels are always whole: the row layout made room for them.
      if (item.label && placed.inside)
        text(ctx, item.label, x + 5, cy + LABEL_SIZE * 0.36, {
          size: LABEL_SIZE,
          weight: 600,
          colour: PALETTE.onAccent,
        });
      else if (item.label && placed.labelX !== undefined)
        text(ctx, item.label, placed.labelX, cy + LABEL_SIZE * 0.36, {
          size: LABEL_SIZE,
          weight: 600,
          colour: PALETTE.text,
        });
      region = { x: r.x - 2, y: r.y - 2, w: Math.max(12, r.w + 4), h: r.h + 4 };
    } else {
      const rad = Math.max(4, Math.min(7, rh * 0.28));
      ctx.fillStyle = item.colour;
      ctx.strokeStyle = item.colour;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      if (item.mark === 'dot') {
        ctx.arc(x, cy, hovered ? rad + 2 : rad, 0, Math.PI * 2);
        ctx.fill();
      } else if (item.mark === 'ring') {
        ctx.arc(x, cy, hovered ? rad + 2 : rad, 0, Math.PI * 2);
        ctx.stroke();
      } else if (item.mark === 'diamond') {
        const k = hovered ? rad + 3 : rad + 1;
        ctx.moveTo(x, cy - k);
        ctx.lineTo(x + k, cy);
        ctx.lineTo(x, cy + k);
        ctx.lineTo(x - k, cy);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(x - 1.5, cy - rh * 0.32, 3, rh * 0.64);
      }
      // Visit codes above the dots when they fit before the next one.
      if (item.label && lane === 'Visits') {
        ctx.font = font(11, 600);
        const tw = ctx.measureText(item.label).width;
        if (nextX === undefined || nextX - x > tw + 4)
          text(ctx, item.label, x, cy - rad - 3, {
            size: 11,
            colour: PALETTE.muted,
            align: 'center',
          });
      } else if (item.label) {
        text(ctx, item.label, x + rad + 3, cy + 4, { size: 11, colour: PALETTE.muted });
      }
      region = { x: x - 8, y: cy - 8, w: 16, h: 16 };
    }
    panel.region({ id, ...region, data: item.path, tooltip: item.tooltip });
  }

  private select(region: Region): void {
    if (!this.subjectId) return;
    this.handlers.open(this.subjectId, [`subject:${this.subjectId}`, ...(region.data as string[])]);
  }

  dispose(): void {
    this.panel.detach(this.interactive);
    this.panel.dispose();
  }
}
